/* Individual gestures and what the person did with each one, shared by the
   customer sheet (one person) and the Gestures section (everyone). The outcome
   comes from /admin/gestures. */
import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api, humanize, parseDate } from './lib';
import { Pill } from './ui';

export const OUTCOME = {
  done: { label: 'Done', tone: 'success', hint: 'Marked as done' },
  already_does: { label: 'Already does it', tone: 'accent', hint: 'Answered “I already do this”' },
  skipped: { label: 'Skipped', tone: 'warning', hint: 'Tapped skip' },
  disliked: { label: 'Disliked', tone: 'danger', hint: 'Marked as disliked' },
  postponed: { label: 'Postponed', tone: 'info', hint: 'Put off for later' },
  no_answer: { label: 'No answer', tone: 'neutral', hint: 'The day ended without done or skip' },
  waiting: { label: 'Today', tone: 'info', hint: 'Given today, so there is still time' },
  swapped: { label: 'Swapped', tone: 'neutral', hint: 'Replaced by another gesture the same day' },
};

const SOURCE_LABEL = {
  ai_refine: 'AI-tailored',
  ai_generated: 'AI-written',
  free_deterministic: 'From the library',
  trial_static: 'Trial plan',
};

/* "All" leaves swapped gestures out: each "another one" leaves the old gesture
   behind, and they would bury what the person was actually left with. */
export const OUTCOME_FILTERS = ['all', 'done', 'already_does', 'skipped', 'disliked', 'postponed', 'no_answer', 'waiting', 'swapped'];

export function outcomeOptions(counts = {}) {
  const shown = (counts.total ?? 0) - (counts.swapped ?? 0);
  return OUTCOME_FILTERS
    .filter((f) => f === 'all' || counts[f] > 0 || ['done', 'skipped', 'no_answer'].includes(f))
    .map((f) => ({ value: f, label: f === 'all' ? 'All' : OUTCOME[f].label, count: f === 'all' ? shown : counts[f] ?? 0 }));
}

export const matchesOutcome = (g, filter) => (filter === 'all' ? g.outcome !== 'swapped' : g.outcome === filter);

export function gestureCounts(gestures = []) {
  const counts = { total: gestures.length };
  gestures.forEach((g) => { counts[g.outcome] = (counts[g.outcome] || 0) + 1; });
  return counts;
}

export const fetchGestures = (params) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null)).toString();
  return api.get(`/admin/gestures${qs ? `?${qs}` : ''}`);
};

export function OutcomePill({ outcome }) {
  const o = OUTCOME[outcome] || OUTCOME.no_answer;
  return <span title={o.hint}><Pill tone={o.tone} dot={outcome === 'done'}>{o.label}</Pill></span>;
}

const fmtStamp = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
};

/* Gesture days are the person's local calendar day (YYYY-MM-DD). */
export function fmtGestureDay(day, { weekday = true } = {}) {
  if (!day) return '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-US', {
    weekday: weekday ? 'short' : undefined,
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
}

export function gestureMeta(g) {
  return [
    humanize(g.category),
    g.estimated_time ? `${g.estimated_time} min` : null,
    g.difficulty ? `${humanize(g.difficulty)} effort` : null,
    SOURCE_LABEL[g.source],
  ].filter(Boolean);
}

/* Whether the app logged this gesture on screen. Views are only recorded
   inside the tracking window (and only with analytics consent), so outside it
   say nothing rather than "not seen". */
function seenLabel(g, tracking) {
  if (g.shown_count > 0) return g.shown_count === 1 ? 'Seen once' : `Seen ${g.shown_count} times`;
  const since = tracking?.since?.slice(0, 10);
  const until = tracking?.until?.slice(0, 10);
  if (since && until && g.date >= since && g.date <= until) return 'Never opened';
  return null;
}

function answeredLabel(g) {
  const at = fmtStamp(g.answered_at || g.completed_at);
  if (!at) return null;
  if (g.outcome === 'done') return `Done ${at}`;
  if (g.outcome === 'skipped') return `Skipped ${at}`;
  if (g.outcome === 'already_does') return `Answered ${at}`;
  return null;
}

export function GestureItem({ g, tracking, muted = false }) {
  const facts = [...gestureMeta(g), answeredLabel(g), seenLabel(g, tracking)].filter(Boolean);
  return (
    <li className={cn('px-4 py-3', muted && 'bg-secondary/30')}>
      <div className="flex items-start justify-between gap-3">
        <p className={cn('min-w-0 text-sm', muted ? 'text-muted-foreground' : 'font-medium text-foreground')}>
          {g.text || 'Untitled gesture'}
        </p>
        <span className="shrink-0"><OutcomePill outcome={g.outcome} /></span>
      </div>
      {g.description && !muted && (
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{g.description}</p>
      )}
      {g.original_text && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Wrote their own instead of: <span className="italic">“{g.original_text}”</span>
        </p>
      )}
      {g.notes && (
        <p className="mt-2 border-l-2 border-border pl-2.5 text-xs italic text-foreground/80">“{g.notes}”</p>
      )}
      {facts.length > 0 && <p className="mt-1.5 text-[11px] text-muted-foreground">{facts.join(' · ')}</p>}
    </li>
  );
}

/* One person's gestures, newest day first. With the "All" filter each day
   keeps its swapped gestures folded away under the ones that counted. */
export function GestureTimeline({ gestures, filter, tracking, pageSize = 40 }) {
  const [limit, setLimit] = useState(pageSize);
  const [openDays, setOpenDays] = useState(() => new Set());

  const days = useMemo(() => {
    const byDay = new Map();
    gestures.forEach((g) => {
      if (!byDay.has(g.date)) byDay.set(g.date, { day: g.date, items: [], swapped: [] });
      const entry = byDay.get(g.date);
      if (matchesOutcome(g, filter)) entry.items.push(g);
      else if (filter === 'all' && g.outcome === 'swapped') entry.swapped.push(g);
    });
    return [...byDay.values()].filter((d) => d.items.length > 0 || d.swapped.length > 0);
  }, [gestures, filter]);

  let budget = limit;
  const visible = [];
  for (const d of days) {
    if (budget <= 0) break;
    visible.push(d);
    budget -= Math.max(1, d.items.length);
  }
  const hidden = days.length - visible.length;

  const toggle = (day) => setOpenDays((prev) => {
    const next = new Set(prev);
    if (next.has(day)) next.delete(day); else next.add(day);
    return next;
  });

  return (
    <div className="space-y-4">
      {visible.map(({ day, items, swapped }) => (
        <section key={day}>
          <h4 className="mb-1.5 text-xs font-medium text-muted-foreground">{fmtGestureDay(day)}</h4>
          <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
            {items.map((g) => <GestureItem key={g.suggestion_id} g={g} tracking={tracking} />)}
            {swapped.length > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => toggle(day)}
                  className="flex w-full items-center gap-1.5 px-4 py-2 text-left text-xs text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                  aria-expanded={openDays.has(day)}
                >
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !openDays.has(day) && '-rotate-90')} />
                  {items.length === 0 ? 'Swapped for another gesture' : 'Also swapped'} {swapped.length === 1 ? '1 gesture' : `${swapped.length} gestures`}
                </button>
              </li>
            )}
            {openDays.has(day) && swapped.map((g) => <GestureItem key={g.suggestion_id} g={g} tracking={tracking} muted />)}
          </ul>
        </section>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setLimit((n) => n + pageSize)}
          className="w-full rounded-lg border border-dashed border-border py-2 text-xs font-medium text-muted-foreground hover:border-foreground/30 hover:text-foreground"
        >
          Show earlier days ({hidden} more)
        </button>
      )}
    </div>
  );
}
